import { useEffect } from 'react';
import type { ReactNode } from 'react';
import useGameStore from '../store/gameStore';
import PlayerLedgerHud from './PlayerLedgerHud';
import { useT } from '../i18n';
import { money } from '../lib/format';
import { BankIcon, ChartIcon, DiceIcon, UsersIcon } from './GameIcons';

export type MobileSheet = 'portfolio' | 'rivals' | null;

interface MobileNavProps {
  /** Active board/turn view flag — highlights the Deska tab. */
  boardActive: boolean;
  /** Opens the Financial Desk modal (loans / funds / insurance). */
  onOpenFinance: () => void;
  /** Portfolio / rivals bottom sheets. */
  sheet: MobileSheet;
  onSheetChange: (sheet: MobileSheet) => void;
  /** Full sidebar content rendered inside the Portfolio sheet. */
  portfolioContent: React.ReactNode;
  /** Attention dot: the human has an actionable decision waiting. */
  actionDot: boolean;
}

/** Native-feeling bottom sheet / drawer used by Portfolio and Rivals. */
export const MobileBottomSheet = ({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm" />
      <div
        className="relative flex h-[80vh] w-full flex-col rounded-t-3xl border-t border-slate-700 bg-slate-900 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="mx-auto flex h-8 w-full shrink-0 items-center justify-center text-slate-500"
          aria-label="Close"
        >
          <span className="h-1.5 w-12 rounded-full bg-slate-600" />
        </button>
        <p className="shrink-0 px-4 pb-2 text-[11px] font-bold uppercase tracking-[0.2em] text-amber-300">{title}</p>
        <div className="min-h-0 flex-1 overflow-y-auto p-4 pt-0 pb-safe scrollbar-thin">{children}</div>
      </div>
    </div>
  );
};

/** Rivals sheet body: live ledger + net-worth standings. */
const RivalsSheetBody = () => {
  const store = useGameStore();
  const t = useT();
  const standings = [...store.players].sort((a, b) => store.netWorthOf(b.id) - store.netWorthOf(a.id));
  return (
    <div className="space-y-3">
      <PlayerLedgerHud />
      <div className="glass rounded-2xl p-3">
        <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-slate-400">{t('common.netWorth')}</p>
        <div className="space-y-1.5">
          {standings.map((player) => (
            <div key={player.id} className="flex items-center justify-between gap-2 rounded-xl border border-slate-700/60 bg-slate-950/60 px-2.5 py-2">
              <span className="flex min-w-0 items-center gap-2 text-xs font-bold text-slate-200">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: player.color }} />
                <span className="truncate">{player.name}</span>
              </span>
              <span className="shrink-0 font-mono text-xs font-black text-amber-200">{money(store.netWorthOf(player.id))}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Sticky mobile bottom navigation (thumb zone): Deska · Portfolio · Finance · Hráči.
 * Portfolio and Hráči open bottom sheets; Finance opens the Financial Desk modal.
 */
const MobileNav = ({ boardActive, onOpenFinance, sheet, onSheetChange, portfolioContent, actionDot }: MobileNavProps) => {
  const t = useT();

  const entries: { id: 'board' | 'portfolio' | 'finance' | 'rivals'; label: string; icon: ReactNode; onClick: () => void }[] = [
    { id: 'board', label: t('nav.board'), icon: <DiceIcon size={19} />, onClick: () => onSheetChange(null) },
    { id: 'portfolio', label: t('nav.portfolio'), icon: <ChartIcon size={19} />, onClick: () => onSheetChange(sheet === 'portfolio' ? null : 'portfolio') },
    { id: 'finance', label: t('nav.finance'), icon: <BankIcon size={19} />, onClick: onOpenFinance },
    { id: 'rivals', label: t('nav.rivals'), icon: <UsersIcon size={19} />, onClick: () => onSheetChange(sheet === 'rivals' ? null : 'rivals') },
  ];

  return (
    <>
      <nav className="fixed inset-x-0 bottom-0 z-40 flex h-16 w-full items-center justify-around border-t border-slate-800 bg-slate-900/95 px-2 pb-safe backdrop-blur">
        {entries.map((tab) => {
          const active = tab.id === 'board' ? boardActive && !sheet : sheet === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={tab.onClick}
              className={`relative flex h-12 min-w-[64px] flex-1 flex-col items-center justify-center gap-0.5 rounded-xl text-[10px] font-bold transition ${
                active ? 'bg-slate-800/90 text-amber-200' : 'text-slate-400 active:bg-slate-800/60'
              }`}
            >
              <span className="text-lg leading-none">{tab.icon}</span>
              <span className="truncate">{tab.label}</span>
              {tab.id === 'board' && actionDot && !active && (
                <span className="absolute right-3 top-1 h-2.5 w-2.5 animate-pulse rounded-full bg-amber-300" />
              )}
            </button>
          );
        })}
      </nav>

      <MobileBottomSheet open={sheet === 'portfolio'} title={t('nav.portfolio')} onClose={() => onSheetChange(null)}>
        {portfolioContent}
      </MobileBottomSheet>
      <MobileBottomSheet open={sheet === 'rivals'} title={t('nav.rivals')} onClose={() => onSheetChange(null)}>
        <RivalsSheetBody />
      </MobileBottomSheet>
    </>
  );
};

export default MobileNav;
